import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Pen, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import {
  REASON_TARGET_GROUP_NONE_SELECT_VALUE,
  type ReasonPanelProps,
} from "./reasonPanel.types";
import { useGroupsByOfficeQuery } from "@/hooks/useGroupsByOfficeQuery";
import CustomModal from "@/components/modal/CustomModal";
import { BtnSimpleIcon } from "@/components/ui/button/btn-simple-icon";
import {
  intervalOverlapsRanges,
  formatDateString,
  isDateInAnyRanges,
  isBeforeMinDate,
  parseDateString,
  rangesOverlap,
  type DateRangeYmd,
} from "./datePickerRules";

type DispatchActivity = {
  assignmentId?: number;
  startDate: string;
  endDate: string;
  memo: string | null;
  targetGroupId: string;
};

const EMPTY_DISPATCH_ACTIVITY: DispatchActivity = {
  startDate: "",
  endDate: "",
  targetGroupId: "",
  memo: null,
};

export default function DispatchReasonPanel({
  joiningDate,
  isStartDateError,
  isEndDateError,
  isTargetGroupError,
  excludeTargetGroupId,
  disabled,
  useOfficeGroupsForTarget,
  officeIdForTargetGroups,
  dispatchActivities,
  onDispatchActivitiesChange,
  occupiedRanges = [],
}: ReasonPanelProps) {
  const oid = (officeIdForTargetGroups ?? "").trim();
  const useOffice = useOfficeGroupsForTarget === true && oid.length > 0;

  const { data: officeGroups } = useGroupsByOfficeQuery({
    enabled: useOffice,
    officeId: oid || undefined,
  });

  const excludeId = excludeTargetGroupId?.trim() ?? "";

  const groups = useMemo(() => {
    const raw = useOffice ? (officeGroups ?? []) : [];
    return excludeId === "" ? raw : raw.filter((g) => g.group_id !== excludeId);
  }, [useOffice, officeGroups, excludeId]);

  const activities = useMemo(
    () => dispatchActivities ?? [],
    [dispatchActivities]
  );
  const canEdit = !(disabled ?? false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<DispatchActivity>(EMPTY_DISPATCH_ACTIVITY);
  const [draftErrors, setDraftErrors] = useState({
    startDate: false,
    endDate: false,
    targetGroupId: false,
  });
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(
    null
  );
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);
  const [rangeOverlapError, setRangeOverlapError] = useState(false);

  /** 추가/수정 모달에서 제외한 나머지 파견 구간 — 달력에서 선택 불가 */
  const otherDispatchRanges = useMemo(() => {
    return activities
      .filter((_, idx) => editingIndex == null || idx !== editingIndex)
      .filter((a) => a.startDate.trim() !== "" && a.endDate.trim() !== "");
  }, [activities, editingIndex]);
  const occupiedRangesFromOtherReasons = useMemo<DateRangeYmd[]>(
    () =>
      (occupiedRanges ?? []).filter(
        (r) => r.startDate.trim() !== "" && r.endDate.trim() !== ""
      ),
    [occupiedRanges]
  );

  const minStartDate = useMemo(
    () => parseDateString(joiningDate),
    [joiningDate]
  );
  const minEndDate = useMemo(
    () => parseDateString(draft.startDate || joiningDate),
    [draft.startDate, joiningDate]
  );

  const isCalendarDayBlockedByOthers = (date: Date) =>
    isDateInAnyRanges(date, [
      ...otherDispatchRanges,
      ...occupiedRangesFromOtherReasons,
    ]);

  /** 이미 등록된 파견 구간을 달력에서 구간별로 시각 구분 (react-day-picker modifiers) */
  const occupiedRangeModifiers = useMemo(() => {
    const mods: Record<string, (d: Date) => boolean> = {};
    otherDispatchRanges.forEach((a, i) => {
      mods[`dispatch_occ_${i}`] = (date: Date) =>
        isDateInAnyRanges(date, [a]);
    });
    occupiedRangesFromOtherReasons.forEach((a, i) => {
      mods[`dispatch_reason_occ_${i}`] = (date: Date) =>
        isDateInAnyRanges(date, [a]);
    });
    return mods;
  }, [otherDispatchRanges, occupiedRangesFromOtherReasons]);

  const occupiedRangeModifierClassNames = useMemo(() => {
    const tints = [
      "shadow-[inset_0_0_0_999px_rgba(251,191,36,0.28)] rounded-md",
      "shadow-[inset_0_0_0_999px_rgba(125,211,252,0.28)] rounded-md",
      "shadow-[inset_0_0_0_999px_rgba(196,181,253,0.28)] rounded-md",
    ];
    const out: Record<string, string> = {};
    otherDispatchRanges.forEach((_, i) => {
      out[`dispatch_occ_${i}`] = tints[i % tints.length];
    });
    occupiedRangesFromOtherReasons.forEach((_, i) => {
      out[`dispatch_reason_occ_${i}`] = "shadow-[inset_0_0_0_999px_rgba(244,114,182,0.2)] rounded-md";
    });
    return out;
  }, [otherDispatchRanges, occupiedRangesFromOtherReasons]);

  /** 후보 구간 [startStr, endStr]가 다른 파견 구간과 겹치는지 (포함·걸침 모두) */
  const intervalWouldOverlapOthers = useCallback(
    (startStr: string, endStr: string) => {
      if (!startStr || !endStr) return false;
      const overlapsDispatch = otherDispatchRanges.some(
        (o) =>
          o.startDate &&
          o.endDate &&
          rangesOverlap(startStr, endStr, o.startDate, o.endDate)
      );
      if (overlapsDispatch) return true;
      return intervalOverlapsRanges(
        startStr,
        endStr,
        occupiedRangesFromOtherReasons
      );
    },
    [otherDispatchRanges, occupiedRangesFromOtherReasons]
  );

  const draftIntervalOverlapsOthers = useMemo(
    () =>
      Boolean(
        draft.startDate &&
        draft.endDate &&
        intervalWouldOverlapOthers(draft.startDate, draft.endDate)
      ),
    [draft.startDate, draft.endDate, intervalWouldOverlapOthers]
  );

  useEffect(() => {
    if (!excludeId || activities.length === 0 || !onDispatchActivitiesChange)
      return;
    const hasInvalid = activities.some((a) => a.targetGroupId === excludeId);
    if (!hasInvalid) return;
    onDispatchActivitiesChange(
      activities.map((a) =>
        a.targetGroupId === excludeId ? { ...a, targetGroupId: "" } : a
      )
    );
  }, [activities, excludeId, onDispatchActivitiesChange]);

  const closeDispatchModal = () => {
    setIsModalOpen(false);
    setStartPickerOpen(false);
    setEndPickerOpen(false);
    setRangeOverlapError(false);
  };

  const openAddModal = () => {
    setEditingIndex(null);
    setDraft(EMPTY_DISPATCH_ACTIVITY);
    setDraftErrors({ startDate: false, endDate: false, targetGroupId: false });
    setRangeOverlapError(false);
    setStartPickerOpen(false);
    setEndPickerOpen(false);
    setIsModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setDraft(activities[index] ?? EMPTY_DISPATCH_ACTIVITY);
    setDraftErrors({ startDate: false, endDate: false, targetGroupId: false });
    setRangeOverlapError(false);
    setStartPickerOpen(false);
    setEndPickerOpen(false);
    setIsModalOpen(true);
  };

  const commitDraft = () => {
    setRangeOverlapError(false);
    const errors = {
      startDate: draft.startDate.trim() === "",
      endDate: draft.endDate.trim() === "",
      targetGroupId: draft.targetGroupId.trim() === "",
    };
    setDraftErrors(errors);
    if (errors.startDate || errors.endDate || errors.targetGroupId) return;

    const start = parseDateString(draft.startDate);
    const end = parseDateString(draft.endDate);
    if (start && end && end < start) {
      setDraftErrors((prev) => ({ ...prev, endDate: true }));
      return;
    }

    if (
      draft.startDate &&
      draft.endDate &&
      intervalWouldOverlapOthers(draft.startDate, draft.endDate)
    ) {
      setRangeOverlapError(true);
      return;
    }

    if (!onDispatchActivitiesChange) return;

    if (editingIndex == null) {
      onDispatchActivitiesChange([draft, ...activities]);
    } else {
      onDispatchActivitiesChange(
        activities.map((item, idx) => (idx === editingIndex ? draft : item))
      );
    }

    closeDispatchModal();
    setEditingIndex(null);
    setDraft(EMPTY_DISPATCH_ACTIVITY);
  };

  const removeActivity = () => {
    if (deleteConfirmIndex == null || !onDispatchActivitiesChange) return;
    onDispatchActivitiesChange(
      activities.filter((_, idx) => idx !== deleteConfirmIndex)
    );
    setDeleteConfirmIndex(null);
  };

  const targetGroupSelectValue =
    draft.targetGroupId.trim() === ""
      ? REASON_TARGET_GROUP_NONE_SELECT_VALUE
      : draft.targetGroupId;

  return (
    <div className="flex flex-col gap-3 ">
      <div className="flex justify-between pt-4 font-semibold border-b">
        <span className="text-base font-pretendard text-muted-foreground">
          파견 기록
        </span>
      </div>
      {activities.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#D6D6DE] px-3 py-4 text-sm text-muted-foreground">
          등록된 파견 기록이 없습니다.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {activities.map((item, idx) => {
            const groupName =
              groups.find((g) => g.group_id === item.targetGroupId)
                ?.group_name ?? item.targetGroupId;
            return (
              <div
                key={`${item.assignmentId ?? "new"}-${idx}`}
                className={cn(
                  "rounded-md border grid px-3 py-2",
                  canEdit ? "grid-cols-[1fr_auto]" : "grid-cols-[1fr]"
                )}
              >
                <div className="flex min-w-0 flex-col font-pretendard">
                  <div className="flex items-center gap-2">
                    <p className="text-base text-foreground  ">
                      {groupName || "-"}
                    </p>

                    <p className="text-sm font-medium text-muted-foreground">
                      {item.startDate} ~ {item.endDate}
                    </p>
                  </div>

                  <p className="mt-1 min-w-0 text-xs text-muted-foreground">
                    <span className="block truncate">{item.memo ?? "-"}</span>
                  </p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <BtnSimpleIcon
                      tone="default"
                      onClick={() => openEditModal(idx)}
                      aria-label="파견 기록 수정"
                      icon={<Pen className="size-3.5" />}
                    />
                    <BtnSimpleIcon
                      tone="destructive"
                      onClick={() => setDeleteConfirmIndex(idx)}
                      aria-label="파견 기록 삭제"
                      icon={<Trash2 className="size-3.5" />}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}{" "}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!canEdit}
        onClick={openAddModal}
        className="h-9 px-3 text-[13px]"
      >
        <Plus className="size-3.5 mr-1" />
        추가하기
      </Button>
      {(isStartDateError || isEndDateError || isTargetGroupError) && (
        <p className="text-destructive text-xs">
          * 파견 기록의 시작일/종료일/대상 병동은 필수입니다.
        </p>
      )}
      <CustomModal.Confirm
        isOpen={isModalOpen}
        onClose={closeDispatchModal}
        onConfirm={commitDraft}
        confirmText={editingIndex == null ? "추가" : "수정"}
        cancelText="닫기"
        contentClassName="w-[520px]"
      >
        <div className="flex flex-col gap-3 pb-5">
          <h2 className="pt-1 font-pretendard text-2xl mb-4 text-sig text-center font-semibold">
            파견 근무 설정
          </h2>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2">
            <label className="text-[14px] font-semibold text-[#939393]">
              파견 시작일 <span className="text-[#C0392B]">*</span>
            </label>
            <span />
            <label className="text-[14px] font-semibold text-[#939393]">
              파견 종료일 <span className="text-[#C0392B]">*</span>
            </label>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex-1 min-w-0">
              <Popover open={startPickerOpen} onOpenChange={setStartPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "w-full h-[35px] justify-between px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0",
                      draftErrors.startDate
                        ? "border-destructive"
                        : "border-[#D6D6DE]"
                    )}
                  >
                    <span className="truncate">
                      {draft.startDate || "YYYY-MM-DD"}
                    </span>
                    <ChevronDown className="h-4 w-4 text-[#939393] shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parseDateString(draft.startDate)}
                    onSelect={(date) => {
                      const value = date ? formatDateString(date) : "";
                      setDraft((prev) => ({ ...prev, startDate: value }));
                      setDraftErrors((prev) => ({ ...prev, startDate: false }));
                      setRangeOverlapError(false);
                      setStartPickerOpen(false);
                    }}
                    disabled={(date) => {
                      if (isBeforeMinDate(date, minStartDate)) return true;
                      if (isCalendarDayBlockedByOthers(date)) return true;
                      if (draft.endDate) {
                        const startStr = formatDateString(date);
                        if (intervalWouldOverlapOthers(startStr, draft.endDate))
                          return true;
                      }
                      return false;
                    }}
                    modifiers={occupiedRangeModifiers}
                    modifiersClassNames={occupiedRangeModifierClassNames}
                    captionLayout="label"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <span className="text-[#B2B2B8]">~</span>

            <div className="flex-1 min-w-0">
              <Popover open={endPickerOpen} onOpenChange={setEndPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!draft.startDate}
                    className={cn(
                      "w-full h-[35px] justify-between px-[12px] py-2 text-[15px] font-normal hover:bg-white min-w-0",
                      draftErrors.endDate
                        ? "border-destructive"
                        : "border-[#D6D6DE]"
                    )}
                  >
                    <span className="truncate">
                      {draft.endDate || "YYYY-MM-DD"}
                    </span>
                    <ChevronDown className="h-4 w-4 text-[#939393] shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parseDateString(draft.endDate)}
                    onSelect={(date) => {
                      const value = date ? formatDateString(date) : "";
                      setDraft((prev) => ({ ...prev, endDate: value }));
                      setDraftErrors((prev) => ({ ...prev, endDate: false }));
                      setRangeOverlapError(false);
                      setEndPickerOpen(false);
                    }}
                    disabled={(date) => {
                      if (isBeforeMinDate(date, minEndDate)) return true;
                      if (isCalendarDayBlockedByOthers(date)) return true;
                      if (draft.startDate) {
                        const endStr = formatDateString(date);
                        if (intervalWouldOverlapOthers(draft.startDate, endStr))
                          return true;
                      }
                      return false;
                    }}
                    modifiers={occupiedRangeModifiers}
                    modifiersClassNames={occupiedRangeModifierClassNames}
                    captionLayout="label"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <p className="text-[12px] leading-snug text-muted-foreground">
            *파견이 지정된 날짜는 선택할 수 없습니다.
          </p>
          {(rangeOverlapError || draftIntervalOverlapsOthers) && (
            <p className="text-destructive text-[12px]">
              선택한 기간이 다른 파견 기록과 겹치거나 포함됩니다.
            </p>
          )}
          <div className="flex flex-col gap-2">
            <label className="text-[14px] font-semibold text-[#939393]">
              대상 병동 <span className="text-[#C0392B]">*</span>
            </label>
            <Select
              value={targetGroupSelectValue}
              onValueChange={(value) => {
                setDraft((prev) => ({
                  ...prev,
                  targetGroupId:
                    value === REASON_TARGET_GROUP_NONE_SELECT_VALUE
                      ? ""
                      : value,
                }));
                setDraftErrors((prev) => ({ ...prev, targetGroupId: false }));
              }}
            >
              <SelectTrigger
                className={cn(
                  "w-full text-[14px]",
                  draftErrors.targetGroupId
                    ? "border-destructive"
                    : "border-[#D6D6DE]"
                )}
              >
                <SelectValue placeholder="병동 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={REASON_TARGET_GROUP_NONE_SELECT_VALUE}>
                  선택 안 함
                </SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.group_id} value={g.group_id}>
                    {g.group_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[14px] font-semibold text-[#939393]">
              사유
            </label>
            <Textarea
              className="resize-none min-h-[64px] text-sm"
              placeholder="사유를 입력하세요."
              value={draft.memo ?? ""}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, memo: e.target.value }))
              }
            />
          </div>
        </div>
      </CustomModal.Confirm>
      <CustomModal.Confirm
        isOpen={deleteConfirmIndex != null}
        onClose={() => setDeleteConfirmIndex(null)}
        onConfirm={removeActivity}
        title={
          <div className="flex flex-col gap-1.5">
            <span>파견 기록을 삭제하시겠습니까?</span>
            <span className="text-sm text-muted-foreground">
              기존 근무표는 변경되지 않으며, 신규 근무표부터 적용됩니다.
            </span>
          </div>
        }
      />
    </div>
  );
}
